import { useQuery } from 'react-query';
import { apiClient } from '@/lib/api-client';

function BarChart({ data, color = 'bg-brand-500' }: { data: { label: string; value: number }[]; color?: string }) {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div className="flex items-end gap-1 h-32">
      {data.map(d => (
        <div key={d.label} className="flex-1 flex flex-col items-center gap-1 min-w-0">
          <span className="text-xs text-gray-500 dark:text-zinc-500 font-medium">{d.value}</span>
          <div className={`w-full rounded-t ${color} transition-all`} style={{ height: `${(d.value / max) * 100}%`, minHeight: d.value > 0 ? '4px' : '0' }} />
          <span className="text-xs text-gray-400 dark:text-zinc-600 truncate w-full text-center">{d.label}</span>
        </div>
      ))}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 p-5">
      <h2 className="font-semibold mb-4">{title}</h2>
      {children}
    </div>
  );
}

export default function AdminAnalyticsPage() {
  const { data, isLoading } = useQuery('admin-analytics',
    () => apiClient.get<any>('/admin/analytics')
  );

  if (isLoading) {
    return (
      <div className="p-8 grid grid-cols-2 gap-6">
        {[...Array(4)].map((_,i) => <div key={i} className="h-56 rounded-xl bg-gray-100 dark:bg-zinc-800 animate-pulse" />)}
      </div>
    );
  }

  const toChartData = (rows: any[]) =>
    rows.map(r => ({ label: new Date(r.day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }), value: parseInt(r.count) }));

  const signups = toChartData(data?.user_growth ?? []);
  const listings = toChartData(data?.listing_activity ?? []);
  const trades   = toChartData(data?.trade_activity ?? []);
  const topCards: any[] = data?.top_cards ?? [];

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold mb-1">Analytics</h1>
      <p className="text-sm text-gray-500 dark:text-zinc-500 mb-6">Last 14 days</p>

      <div className="grid md:grid-cols-2 gap-6">
        <Section title="New Signups">
          {signups.length > 0 ? <BarChart data={signups} color="bg-brand-500" /> : <p className="text-sm text-gray-400 dark:text-zinc-600">No data yet</p>}
        </Section>
        <Section title="New Listings">
          {listings.length > 0 ? <BarChart data={listings} color="bg-green-500" /> : <p className="text-sm text-gray-400 dark:text-zinc-600">No data yet</p>}
        </Section>
        <Section title="Trade Activity">
          {trades.length > 0 ? <BarChart data={trades} color="bg-purple-500" /> : <p className="text-sm text-gray-400 dark:text-zinc-600">No data yet</p>}
        </Section>
      </div>

      {topCards.length > 0 && (
        <div className="mt-6">
          <Section title="Top Listed Cards (30 days)">
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 dark:text-zinc-500 uppercase">
                <tr>
                  <th className="pb-2 text-left">Card</th>
                  <th className="pb-2 text-right">Listings</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                {topCards.map((c: any) => (
                  <tr key={c.name}>
                    <td className="py-2 font-medium">{c.name}</td>
                    <td className="py-2 text-right text-gray-600 dark:text-zinc-400">{c.listing_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        </div>
      )}
    </div>
  );
}
